/**
 * Was beim Auslesen einer Belegdatei herauskam.
 *
 * ---------------------------------------------------------------------
 * Warum das ein eigener Block ist und kein vorbefülltes Formularfeld
 *
 * Ein Vorschlag, der aussieht wie ein eingegebener Wert, wird zu einem
 * eingegebenen Wert, ohne dass jemand hingesehen hat. Auf einem Kassenbon
 * stehen „Zwischensumme 10,28" und „SUMME 9,28" untereinander; wer den
 * falschen übernimmt, bucht siebzig Cent zu viel und merkt es nie.
 *
 * Deshalb steht hier neben jedem Wert, woher er kommt und wie eindeutig
 * der Fund war — und daneben ein Knopf, der ihn übernimmt. Der Klick ist
 * die Stelle, an der aus einem Vorschlag ein Wert wird.
 */
import { useEffect, type ReactNode } from 'react';
import { datum } from '../lib/format';
import type { Beleg, BelegVorschlaege } from '../lib/typen';

export function Vorschlaege({
  beleg,
  darfSchreiben,
  uebernehmen,
  laeuft,
  neuLaden,
}: {
  beleg: Beleg;
  darfSchreiben: boolean;
  uebernehmen: (werte: Record<string, string | number>) => Promise<void>;
  laeuft: boolean;
  neuLaden: () => void;
}): ReactNode {
  const laeuftNoch = beleg.dateien.some(
    (d) => d.ocrStatus === 'offen' || d.ocrStatus === 'laeuft',
  );

  /*
   * Solange etwas läuft, alle paar Sekunden nachfragen.
   *
   * Das Auslesen passiert nach der Antwort auf den Upload — sonst
   * wartete die App mit drehendem Rad, bis ein Foto erkannt ist. Ohne
   * Nachfragen bliebe die Karte leer, bis jemand die Seite neu lädt, und
   * niemand käme auf die Idee, dass da noch etwas kommt.
   */
  useEffect(() => {
    if (!laeuftNoch) return;
    const uhr = setTimeout(neuLaden, 2500);
    return () => clearTimeout(uhr);
  }, [laeuftNoch, neuLaden, beleg.dateien]);

  if (beleg.dateien.length === 0) return null;

  if (laeuftNoch) {
    return (
      <Karte>
        <p className="klein still" style={{ margin: 0 }}>
          Wird ausgelesen …
        </p>
      </Karte>
    );
  }

  const mitVorschlag = beleg.dateien.filter(
    (d) => d.ocrStatus === 'fertig' && d.ocrFelder && felderAus(d.ocrFelder).length > 0,
  );

  if (mitVorschlag.length === 0) {
    /*
     * Warum nichts kam, gehört gesagt.
     *
     * Sonst sieht „kein Vorschlag" aus wie ein Fehler — und bei einem
     * HEIC-Foto oder einem eingescannten PDF ohne Textebene ist es
     * keiner, sondern eine Eigenschaft der Datei.
     */
    const hinweis = beleg.dateien
      .map((d) => d.ocrFelder?.hinweis)
      .find((h): h is string => typeof h === 'string' && h !== '');
    if (!hinweis) return null;

    return (
      <Karte>
        <p className="klein still" style={{ margin: 0 }}>
          {hinweis}
        </p>
      </Karte>
    );
  }

  return (
    <Karte>
      {mitVorschlag.map((d) => {
        const felder = felderAus(d.ocrFelder!);
        const offen = felder.filter((f) => f.feld !== null && !stimmtUeberein(f, beleg));

        return (
          <div key={d.id} style={{ marginBottom: '0.75rem' }}>
            <div className="klein still" style={{ marginBottom: '0.375rem' }}>
              {d.dateinameOriginal} ·{' '}
              {d.ocrFelder?.quelle === 'bilderkennung'
                ? 'aus dem Bild erkannt'
                : 'aus dem PDF gelesen'}
            </div>

            <table className="positionen">
              <tbody>
                {felder.map((f) => (
                  <tr key={f.schluessel}>
                    <td style={{ width: '9rem' }}>{f.beschriftung}</td>
                    <td>
                      <strong>{f.anzeige}</strong>{' '}
                      <span className="klein still">{gueteText(f.konfidenz)}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {f.feld === null ? null : stimmtUeberein(f, beleg) ? (
                        <span className="klein still">übernommen</span>
                      ) : darfSchreiben ? (
                        <button
                          type="button"
                          className="knopf schlicht klein"
                          disabled={laeuft}
                          onClick={() => void uebernehmen({ [f.feld!]: wertFuerApi(f) })}
                        >
                          übernehmen
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {darfSchreiben && offen.length > 1 ? (
              <button
                type="button"
                className="knopf klein"
                style={{ marginTop: '0.5rem' }}
                disabled={laeuft}
                onClick={() =>
                  void uebernehmen(Object.fromEntries(offen.map((f) => [f.feld!, wertFuerApi(f)])))
                }
              >
                Alle übernehmen
              </button>
            ) : null}
          </div>
        );
      })}

      <p className="klein still" style={{ margin: '0.5rem 0 0' }}>
        Vorschläge, keine Werte. Was hier steht, wandert erst durch einen Klick in
        den Beleg.
      </p>
    </Karte>
  );
}

function Karte({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Aus dem Beleg gelesen</h2>
      </div>
      <div className="karte-koerper">{children}</div>
    </div>
  );
}

type Erkanntes = {
  schluessel: string;
  beschriftung: string;
  wert: string;
  anzeige: string;
  konfidenz: number;
  /** Auf welches Belegfeld es übernommen wird. Ohne Feld: nur Anzeige. */
  feld: string | null;
  /*
   * Ob der Wert als Zahl hinausgeht.
   *
   * Die API nimmt den Betrag als Text an — "120,79" wird dort über
   * centAusEingabe gelesen, damit ein deutsches Komma nicht stillschweigend
   * zum Tausendertrenner wird. Der Steuersatz ist dagegen eine Zahl. Ohne
   * diese Unterscheidung antwortet die API mit
   * "steuersatz: Expected number, received string", und der Knopf tut
   * scheinbar nichts.
   */
  alsZahl?: boolean;
};

/** Die Vorschläge einer Datei in Anzeigereihenfolge. */
function felderAus(v: BelegVorschlaege): Erkanntes[] {
  const felder: Erkanntes[] = [];

  if (v.betragBrutto) {
    felder.push({
      schluessel: 'betragBrutto',
      beschriftung: 'Betrag',
      wert: v.betragBrutto.wert,
      anzeige: `${v.betragBrutto.wert} €`,
      konfidenz: v.betragBrutto.konfidenz,
      feld: 'betragBrutto',
    });
  }
  if (v.belegDatum) {
    felder.push({
      schluessel: 'belegDatum',
      beschriftung: 'Belegdatum',
      wert: v.belegDatum.wert,
      anzeige: datum(v.belegDatum.wert),
      konfidenz: v.belegDatum.konfidenz,
      feld: 'belegDatum',
    });
  }
  if (v.steuersatz) {
    felder.push({
      schluessel: 'steuersatz',
      beschriftung: 'Steuersatz',
      wert: v.steuersatz.wert,
      anzeige: `${v.steuersatz.wert} %`,
      konfidenz: v.steuersatz.konfidenz,
      feld: 'steuersatz',
      alsZahl: true,
    });
  }
  if (v.lieferant) {
    felder.push({
      schluessel: 'lieferant',
      beschriftung: 'Lieferant',
      wert: v.lieferant.wert,
      anzeige: v.lieferant.wert,
      konfidenz: v.lieferant.konfidenz,
      /*
       * Wird angezeigt, nicht übernommen.
       *
       * Am Beleg ist der Lieferant eine Kontaktkennung, kein Text. Aus
       * einem gelesenen Namen einen Kontakt anzulegen wäre eine
       * Entscheidung — die trifft ein Mensch, im Dialog neben der
       * Kontaktauswahl.
       */
      feld: null,
    });
  }
  if (v.rechnungsnummer) {
    felder.push({
      schluessel: 'rechnungsnummer',
      beschriftung: 'Rechnungsnr.',
      wert: v.rechnungsnummer.wert,
      anzeige: v.rechnungsnummer.wert,
      konfidenz: v.rechnungsnummer.konfidenz,
      feld: null,
    });
  }
  if (v.iban) {
    felder.push({
      schluessel: 'iban',
      beschriftung: 'IBAN',
      wert: v.iban.wert,
      anzeige: v.iban.wert,
      konfidenz: v.iban.konfidenz,
      feld: null,
    });
  }

  return felder;
}

function wertFuerApi(f: Erkanntes): string | number {
  return f.alsZahl ? Number.parseFloat(f.wert.replace(',', '.')) : f.wert;
}

/** Steht der Wert schon so im Beleg? Dann gibt es nichts zu übernehmen. */
function stimmtUeberein(f: Erkanntes, beleg: Beleg): boolean {
  if (f.feld === 'betragBrutto') return beleg.betragBrutto.replace('.', ',') === f.wert;
  if (f.feld === 'belegDatum') return beleg.belegDatum === f.wert;
  if (f.feld === 'steuersatz') {
    return Number.parseFloat(beleg.steuersatz) === Number.parseFloat(f.wert);
  }
  return false;
}

/**
 * Die Konfidenz in Worte.
 *
 * „0,68" sagt niemandem etwas und suggeriert eine Genauigkeit, die es
 * nicht gibt. Drei Stufen genügen für die Frage, die sich tatsächlich
 * stellt: übernehmen oder lieber nachsehen?
 */
function gueteText(konfidenz: number): string {
  if (konfidenz >= 0.8) return 'eindeutig';
  if (konfidenz >= 0.5) return 'wahrscheinlich';
  return 'geraten — bitte prüfen';
}
